import React from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Gift, Check, Copy, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import Grain from "../components/Grain";

export default function OffrirSuccess() {
  const { giftId } = useParams();
  const [search] = useSearchParams();
  const sessionId = search.get("session_id");

  const [gift, setGift] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [copied, setCopied] = React.useState(false);
  const pollRef = React.useRef(null);

  const fetchGift = React.useCallback(async () => {
    try {
      const r = await api.get(`/gifts/${giftId}`);
      setGift(r.data);
      return r.data;
    } catch {
      return null;
    }
  }, [giftId]);

  // Poll Stripe status until paid
  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // Trigger Stripe status check to finalize gift
      if (sessionId) {
        try { await api.get(`/gifts/payment-status/${sessionId}`); } catch {}
      }
      const data = await fetchGift();
      if (cancelled) return;
      if (data && data.code) {
        setLoading(false);
        return;
      }
      // Not ready yet — poll again in 2s (max 15s)
      pollRef.current = setTimeout(run, 2000);
    };
    run();
    const safety = setTimeout(() => setLoading(false), 15000);
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
      clearTimeout(safety);
    };
  }, [sessionId, fetchGift]);

  const copyCode = async () => {
    if (!gift?.code) return;
    try {
      await navigator.clipboard.writeText(gift.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div className="section-dark min-h-screen pt-28 pb-20 relative" data-testid="offrir-success">
      <Grain />
      <div className="max-w-2xl mx-auto px-6 md:px-12 relative z-10 text-center">
        {loading && !gift?.code && (
          <div className="py-20">
            <Loader2 size={32} className="animate-spin text-accent-light mx-auto" />
            <p className="text-text-cream/60 mt-6 font-light">Finalisation du paiement…</p>
          </div>
        )}
        {gift?.code && (
          <>
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent-light/15 text-accent-light mb-6">
              <Gift size={28} />
            </div>
            <div className="eyebrow mb-3">Carte Cadeau confirmée</div>
            <h1 className="font-serif text-4xl md:text-5xl mb-4">
              Merci <span className="italic-accent">✨</span>
            </h1>
            <p className="text-text-cream/70 font-light mb-10 max-w-lg mx-auto">
              La carte cadeau <strong className="text-text-cream">{gift.service_name}</strong> est prête.
              Le PDF élégant t'est envoyé par email dans les 2 minutes.
            </p>

            <div className="border border-accent-deep/40 bg-accent-deep/5 rounded p-6 mb-8">
              <div className="eyebrow text-accent-deep mb-2">Code cadeau</div>
              <div className="font-mono text-2xl md:text-3xl text-accent-light tracking-[0.2em] mb-4" data-testid="gift-code">
                {gift.code}
              </div>
              <button
                onClick={copyCode}
                className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded border border-white/15 text-text-cream hover:bg-white/5 transition"
                data-testid="copy-gift-code"
              >
                {copied ? <><Check size={12} /> Copié</> : <><Copy size={12} /> Copier le code</>}
              </button>
            </div>

            <div className="text-text-cream/60 text-sm font-light mb-8">
              {gift.recipient_email ? (
                gift.deliver_at && !gift.delivered_at
                  ? `Le destinataire recevra l'email le ${gift.deliver_at}.`
                  : `Email envoyé au destinataire (${gift.recipient_email}).`
              ) : (
                "La carte cadeau est en pièce jointe de ton email — à imprimer ou transférer."
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/" className="btn-primary">
                Retour à l'accueil <ArrowRight size={14} className="ml-2 inline" />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
